import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import colors from '../../theme/colors';
import { Meetup } from '../../services/firebaseService';

interface MeetupModalProps {
  open: boolean;
  onClose: () => void;
  meetup: Meetup | null;
}

const MeetupModal: React.FC<MeetupModalProps> = ({ open, onClose, meetup }) => {
  if (!meetup) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: `2px solid ${colors.green}`
      }}>
        <Typography variant="h5" color="primary" fontWeight={700}>
          {meetup.title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box>
          {/* Image */}
          {meetup.imageUrl && (
            <Box sx={{ mb: 3, textAlign: 'center', marginTop: 5 }}>
              <img
                src={meetup.imageUrl}
                alt={meetup.title}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 300, 
                  objectFit: 'cover', 
                  borderRadius: 12,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          )}
          
          {/* Event Details */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
              Event Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Date
                </Typography>
                <Typography variant="body1">
                  {new Date(meetup.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Speaker
                </Typography>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  {meetup.speakerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {meetup.speakerRole}
                </Typography>
              </Box>
              {meetup.location && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {meetup.location}
                  </Typography>
                </Box>
              )}
              {meetup.numberOfAttendees && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Attendees
                  </Typography>
                  <Typography variant="body1">
                    {meetup.numberOfAttendees}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {meetup.description}
            </Typography>
          </Box>

          {/* Link */}
          {meetup.link && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                Additional Information
              </Typography>
              <Button
                href={meetup.link}
                target="_blank"
                rel="noopener"
                variant="contained"
                color="primary"
                size="large"
                sx={{ fontWeight: 600 }}
              >
                Watch Now
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetupModal;
